import {
  WINDOWS_ANSWER_LABELS,
  WINDOWS_ANSWERS,
  WindowsAnswer,
  WindowsAnswerLabel
} from '@alpinehomeair/common';

// Which of the windows answers (declared once in @alpinehomeair/common,
// labels and engine tokens together) a home of a given age can sensibly be
// asked. Homes built 2010 or later are not asked at all (their era glass is
// already code glass); older homes are not offered a replacement decade
// earlier than the decade after they were built, and 'not sure when' only
// when more than one decade is possible.

const NO_QUESTION_FROM_YEAR = 2010;

type ReplacementDecade = Extract<WindowsAnswer, { replacedThrough: number }>;

const isReplacementDecade = (
  answer: WindowsAnswer
): answer is ReplacementDecade => 'replacedThrough' in answer;

export const ALL_WINDOWS_ANSWERS: WindowsAnswerLabel[] = [
  ...WINDOWS_ANSWER_LABELS
];

const decadeStart = (year: number): number => Math.floor(year / 10) * 10;

// The answers to offer for a home built in `yearBuilt`, in their declared
// order; null when the question should not be asked. An unknown year offers
// every answer.
export function windowAnswerOptions(
  yearBuilt: number | null | undefined
): WindowsAnswerLabel[] | null {
  if (yearBuilt == null || !Number.isFinite(yearBuilt)) {
    return ALL_WINDOWS_ANSWERS;
  }
  if (yearBuilt >= NO_QUESTION_FROM_YEAR) {
    return null;
  }

  const firstReplacementYear = decadeStart(yearBuilt) + 10;
  const possibleDecades = WINDOWS_ANSWERS.filter(
    (answer) =>
      isReplacementDecade(answer) &&
      answer.replacedThrough >= firstReplacementYear
  );

  return WINDOWS_ANSWERS.filter((answer) => {
    if (isReplacementDecade(answer)) {
      return possibleDecades.includes(answer);
    }
    return answer.token === 'original' || possibleDecades.length > 1;
  }).map((answer) => answer.label);
}

// The stored answer if a home of this age can still give it, else null.
export function applicableWindowsAnswer(
  answer: unknown,
  yearBuilt: number | null | undefined
): WindowsAnswerLabel | null {
  if (typeof answer !== 'string' || answer.trim() === '') {
    return null;
  }
  const options = windowAnswerOptions(yearBuilt);
  return options?.find((option) => option === answer) ?? null;
}
